import React, { FC, useRef, useState } from 'react';

import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';

import { Alert, Box, Button, useMediaQuery, useTheme } from '@mui/material';
import Dialog from 'components/dialog';
import FlexBox from 'components/flex-box';
import NumberField from 'components/number-field';
import LoadingSpinner from 'components/loading-spinner';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import Switch from 'components/switch';
import TextField from 'components/text-field';

import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';

import { Treatment } from '../types/treatment.type';
import { CATEGORY_KEYS } from '../types/treatment-category-keys';

import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { editDuplicatedCode } from 'views/self-service/utils/self-service';
import { getCategories } from '../api/get-categories';
import { putTreatment } from '../api/put-treatment';
import { TreatmentType } from '../types/treatment-type.enum';
import { UpdateTreatmentInput } from '../types/update-treatment-input.type';

type Props = {
  idx: number;
  treatment: Treatment;
  treatments: Treatment[] | undefined;
};

type FormValues = {
  code: string;
  type: string;
  brand: string;
  name: string;
  price: number;
  hidden: boolean;
  treatmentId?: string | undefined;
  treatmentCategory: number | string;
};

const PAGE_NAME_ID = 'selfService.treatmentCatalog';

const EditTreatment: FC<Props> = ({ idx, treatment, treatments }) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { account } = useAccountState();
  const snackbarDispatch = useSnackbarDispatch();
  const dialogRef = useRef<React.ElementRef<typeof Dialog>[]>([]);
  const [duplicatedCode, setDuplicatedCode] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // #region FORMS
  const { handleSubmit, control, reset, getValues, setError, clearErrors } = useForm<FormValues>({
    defaultValues: {
      code: '',
      type: '',
      brand: '',
      name: '',
      price: 0,
      hidden: false,
      treatmentId: '',
      treatmentCategory: 0,
    },
    shouldUnregister: true,
  });
  //#endregion FORMS

  // #region FETCH DATA
  const { data: categories } = useGet(['getCategories'], () => getCategories(String(account.company!.companyId)), {
    enabled: !!account.company!.companyId,
  });
  // #endregion FETCH DATA

  // #region PUT DATA
  const updateTreatment = usePost<Treatment, { request: UpdateTreatmentInput }>(
    (request) => putTreatment(account.company!.companyId, Number(treatment.id), request.request),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      queryClient.invalidateQueries({
        queryKey: ['getTreatments']
      });
      dialogRef.current.forEach((dialog) => dialog.setOpen(false));
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );
  // #endregion PUT DATA

  // #region HANDLERS
  const handleUpdateTreatment = (request: FormValues) => {
    updateTreatment.mutate({ request });
  };

  const handleCodeError = (isDuplicate: boolean) => {
    setDuplicatedCode(isDuplicate);
    isDuplicate ? setError('code', {}) : clearErrors('code');
  };
  // #endregion HANDLERS

  return (
    <>
      <Box display="flex" justifyContent={{ xs: 'center', md: 'flex-end' }} alignItems="center">
        <Dialog
          onClose={() => handleCodeError(false)}
          ref={(el) => {
            if (el) return (dialogRef.current[idx] = el);
          }}
          maxWidth="sm"
          title={intl.formatMessage({ id: `${PAGE_NAME_ID}.editTreatmentCatalog.dialog.title` })}
          button={
            <Button
              onClick={() =>
                reset({
                  treatmentId: treatment.id,
                  code: treatment.code,
                  name: treatment.name,
                  type: treatment.type,
                  price: treatment.price,
                  brand: treatment.brand,
                  hidden: treatment.hidden,
                  treatmentCategory:
                    categories?.find((category) => category.name.toLowerCase() === treatment.treatmentCategory)?.id ?? 5,
                })
              }
              size="small"
              sx={{ width: { xs: '100%' }, marginTop: { xs: '5%' } }}>
              {intl.formatMessage({ id: `${PAGE_NAME_ID}.list.edit` })}
            </Button>
          }>
          <form onSubmit={handleSubmit(handleUpdateTreatment)}>
            {updateTreatment.isPending && <LoadingSpinner overlay />}
            <FlexBox>
              {duplicatedCode && (
                <Alert severity="warning">
                  <FormattedMessage id={intl.formatMessage({ id: `${PAGE_NAME_ID}.editTreatmentCatalog.dialog.duplicatedCode` })} />
                </Alert>
              )}
              <TextField
                sx={{ display: 'none' }}
                fullWidth
                label="treatmentId"
                controller={{
                  name: 'treatmentId',
                  control: control,
                }}
              />
              <TextField
                dark
                fullWidth
                controller={{
                  name: 'name',
                  control: control,
                  rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                }}
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.editTreatmentCatalog.dialog.name` })}
              />
              <TextField
                dark
                fullWidth
                maxLength={50}
                onBlur={() => {
                  const codeValue = getValues('code').trim();
                  handleCodeError(
                    codeValue !== '' ? editDuplicatedCode(treatments, codeValue, Number(getValues('treatmentId'))) : false
                  );
                }}
                controller={{
                  name: 'code',
                  control: control,
                }}
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.editTreatmentCatalog.dialog.code` })}
              />
              <TextField
                dark
                maxLength={50}
                fullWidth
                controller={{
                  name: 'brand',
                  control: control,
                  rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                }}
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.editTreatmentCatalog.dialog.brand` })}
              />
              <Box display={'flex'} gap={4} flexWrap={isMobile ? 'wrap' : 'nowrap'}>
                <Select
                  dark
                  fullWidth
                  controller={{
                    name: 'type',
                    control: control,
                    rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                  }}
                  label={intl.formatMessage({ id: `${PAGE_NAME_ID}.editTreatmentCatalog.dialog.type` })}>
                  {Object.values(TreatmentType).map((value, index) => (
                    <RadioSelectItem key={index} value={value}>
                      <FormattedMessage id={`${PAGE_NAME_ID}.types.${value}`} />
                    </RadioSelectItem>
                  ))}
                </Select>
                <Select
                  dark
                  fullWidth
                  controller={{
                    name: 'treatmentCategory',
                    control: control,
                    rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                  }}
                  label={intl.formatMessage({ id: `${PAGE_NAME_ID}.editTreatmentCatalog.dialog.treatmentCategory` })}>
                  {categories?.map((category) => {
                    const translationKey = CATEGORY_KEYS[category.name];
                    return (
                      <RadioSelectItem key={category.id} value={category.id}>
                        <FormattedMessage id={`${PAGE_NAME_ID}.types.${translationKey}`} defaultMessage={category.name} />
                      </RadioSelectItem>
                    );
                  })}
                </Select>
              </Box>
              <NumberField
                dark
                fullWidth
                controller={{
                  name: 'price',
                  control: control,
                  rules: {
                    required: intl.formatMessage({ id: 'form.rules.required' }),
                    validate: {
                      positive: (v) => (parseInt(v) >= 0 ? true : intl.formatMessage({ id: 'form.rules.positive' })),
                    },
                  },
                }}
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.editTreatmentCatalog.dialog.price` })}
              />
              <Switch
                dark
                value={treatment.hidden}
                controller={{
                  name: 'hidden',
                  control: control,
                }}
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.editTreatmentCatalog.dialog.inactive` })}
              />
              <Box display={'flex'} justifyContent={'end'}>
                <Button type="submit" disabled={duplicatedCode}>
                  <FormattedMessage id={`${PAGE_NAME_ID}.editTreatmentCatalog.dialog.button`} />
                </Button>
              </Box>
            </FlexBox>
          </form>
        </Dialog>
      </Box>
    </>
  );
};

export default React.memo(EditTreatment);
